import * as R from 'ramda';
import { v4 as uuid } from 'uuid';

import { Button, Icon, IconButton, TextField } from '@atom/mui';
import { AddIcon, CloseIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { ValueDictionary } from '@atom/types/work';
import {
  preventOptionLineBreak,
  toSingleLineOption,
} from '@atom/utilities/workOrderFieldUtilities';

import classes from './customFieldEnumEdit.module.css';

const MIN_ENUMERATION = 2;

const styles = {
  button: {
    padding: '0',
    marginLeft: '5px',
  },
  addOptionButton: {
    marginTop: '0.5rem',
  },
};

interface Props {
  valueDictionary: ValueDictionary;
  enumeration: string[];
  setEnumeration: (enumeration: string[]) => void;
  setValueDictionary: (valueDictionary: ValueDictionary) => void;
  maxOptions?: number;
}

const CustomFieldEnumMultipleEdit = ({
  valueDictionary,
  setValueDictionary,
  enumeration,
  setEnumeration,
  maxOptions,
}: Props) => {
  const addOption = () => {
    const id = uuid();

    setEnumeration([...enumeration, id]);
    setValueDictionary({
      ...valueDictionary,
      [id]: '',
    });
  };

  const updateOption = (event, key: string) => {
    setValueDictionary({
      ...valueDictionary,
      [key]: toSingleLineOption(event.target.value),
    });
  };

  const removeOption = (key: string) => {
    setEnumeration(R.remove(enumeration.indexOf(key), 1, enumeration));

    setValueDictionary(R.omit([key], valueDictionary));
  };

  const isAtMaxOptions = !!maxOptions && enumeration.length >= maxOptions;

  return (
    <>
      {!!maxOptions && (
        <div className={classes.maxOptionsHelper}>
          {`${maxOptions} max options for list`}
        </div>
      )}
      {enumeration.map((key: string, index: number) => {
        return (
          <div key={key} className={classes.optionInputContainer}>
            <div className={classes.optionNumber}>{`${index + 1}.`}</div>
            <TextField
              id={key}
              value={valueDictionary[key]}
              key={key}
              onChange={event => updateOption(event, key)}
              onKeyDown={preventOptionLineBreak}
              multiline
              data-cy={`customFieldOptionInput-${index}`}
              style={{ width: '100%' }}
            />
            <IconButton
              style={styles.button}
              onClick={(): void => removeOption(key)}
              data-cy={`customFieldOptionRemoveButton-${index}`}
              disabled={enumeration.length <= MIN_ENUMERATION}
            >
              <Icon icon={CloseIcon} />
            </IconButton>
          </div>
        );
      })}
      <div>
        <Button
          color="primary"
          startIcon={
            <Icon
              color={isAtMaxOptions ? colors.neutral.gray : colors.brand.blue}
              icon={AddIcon}
            />
          }
          onClick={addOption}
          style={styles.addOptionButton}
          disabled={isAtMaxOptions}
          data-cy="customFieldAddOptionButton"
        >
          Add Option
        </Button>
      </div>
    </>
  );
};

export default CustomFieldEnumMultipleEdit;
